import type { PropsWithChildren, JSX } from 'react'
import { useEffect, useRef } from 'react'

import { clsx } from 'clsx'
import { useMotionValue, useReducedMotion, useSpring } from 'motion/react'
import * as m from 'motion/react-m'

import styles from './AnimatedTile.module.css'

type AnimatedTileProps = {
  className?: string
} & PropsWithChildren

export function AnimatedTile({
  className,
  children,
}: AnimatedTileProps): JSX.Element {
  const shouldReduceMotion = useReducedMotion()
  const containerRef = useRef<HTMLDivElement>(null)

  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  const smoothOptions = { stiffness: 50, damping: 50, mass: 0.5 }
  const smoothMouseX = useSpring(mouseX, smoothOptions)
  const smoothMouseY = useSpring(mouseY, smoothOptions)

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent): void => {
      if (!containerRef.current) {
        return
      }

      const boundingRect = containerRef.current.getBoundingClientRect()

      const relativeX =
        event.clientX - (boundingRect.left + boundingRect.width / 2)
      const relativeY =
        event.clientY - (boundingRect.top + boundingRect.height / 2)

      const maxDistance = 35
      const distance = Math.hypot(relativeX, relativeY)
      const activationRadius = 400

      if (distance < activationRadius) {
        if (distance <= maxDistance) {
          mouseX.set(relativeX)
          mouseY.set(relativeY)
        } else {
          mouseX.set(relativeX * (maxDistance / distance))
          mouseY.set(relativeY * (maxDistance / distance))
        }
      } else {
        mouseX.set(0)
        mouseY.set(0)
      }
    }

    window.addEventListener('mousemove', handleMouseMove)

    return (): void => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [mouseX, mouseY])

  return (
    <div className={clsx(styles.container, className)} ref={containerRef}>
      <m.div
        className={styles.animatedElement}
        style={
          shouldReduceMotion
            ? { x: 0, y: 0 }
            : { x: smoothMouseX, y: smoothMouseY }
        }
      >
        {children}
      </m.div>
    </div>
  )
}
